/**
 * 검색엔진 쪽 지표 — 엣지 크롤 도달 (260928-oai-searchbot-is-the-index-gate).
 *
 * 전부 **운영자 전용**이다. 우리 사이트가 어디까지 크롤됐는지는 포트폴리오라
 * 고객·대행사에게 내보내지 않는다 (260915-access-roles).
 */
import { z } from 'zod';
import { KstDate, Rate } from '../common.ts';
import { SITE_KINDS } from '../probe-values.ts';
import { defineRoute } from '../route.ts';

const Days = z.object({ days: z.coerce.number().int().min(1).max(90).optional() });

/** Cloudflare zone id — uuid 가 아니라 32자리 16진수다 */
export const ZoneTag = z.string().regex(/^[0-9a-f]{32}$/);

/**
 * 한 창(窓)의 크롤 요청 수.
 * ★ `page` 는 apex·www 만이다 — `asset`(img.*)을 섞으면 R2 이미지 페치를 크롤로 센다.
 * ★ `aiSearch` 가 판정의 기준이다. UA 계열 셋은 (zone,날짜)당 상위 300개만 저장돼
 *   **과소 계상될 수 있다** — 참고로만 본다.
 */
export const CrawlCounts = z.object({
  page: z.number().int(),
  asset: z.number().int(),
  aiSearch: z.number().int(),
  oaiSearchBot: z.number().int(),
  gptbot: z.number().int(),
  googlebot: z.number().int(),
});
export type CrawlCounts = z.infer<typeof CrawlCounts>;

export const crawlSummary = defineRoute({
  method: 'get',
  path: '/api/seo/crawl',
  access: 'admin',
  summary: 'zone별 엣지 크롤 도달과 인용률',
  query: Days,
  response: z.object({
    windowDays: z.number().int(),
    from: KstDate,
    to: KstDate,
    zones: z.array(
      CrawlCounts.extend({
        zoneTag: ZoneTag,
        zoneHost: z.string(),
        /** 창 안에서 실제로 수집된 날 수 — 보존이 ~5~6일이라 창보다 작은 것이 정상이다 */
        daysCollected: z.number().int(),
        lastDate: KstDate.nullable(),
        /** geo_site 에 같은 호스트가 등록돼 있을 때만. 없으면 인용을 셀 수 없다 */
        siteId: z.uuid().nullable(),
        siteName: z.string().nullable(),
        siteKind: z.enum(SITE_KINDS).nullable(),
        /** 분모는 창 안의 **전체 적격 샘플**이다 — 사이트끼리 견주는 용도다 */
        cited: Rate.nullable(),
      }),
    ),
  }),
});

/**
 * 일별 추이. **`null` 과 `0` 을 가른다.**
 *  - `crawl: null` = 그날 수집이 안 됐다 (보존 밖이거나 수집기가 멎었다)
 *  - `cited: null` = 그날 적격 샘플이 0이었다
 * 비율이 아니라 **건수**다 — 분모(전체 적격 샘플)가 고객·질문이 늘면 커져서, 시계열에 비율을
 * 쓰면 크롤·인용이 그대로여도 선이 내려간다.
 */
export const crawlTrend = defineRoute({
  method: 'get',
  path: '/api/seo/crawl/trend',
  access: 'admin',
  summary: 'zone 하나의 일별 크롤 도달과 인용 건수',
  query: Days.extend({ zone: ZoneTag }),
  response: z.object({
    windowDays: z.number().int(),
    zoneTag: ZoneTag,
    zoneHost: z.string(),
    siteId: z.uuid().nullable(),
    daily: z.array(
      z.object({
        date: KstDate,
        crawl: CrawlCounts.nullable(),
        cited: z.object({ k: z.number().int(), n: z.number().int() }).nullable(),
      }),
    ),
  }),
});
